import { useCallback, useEffect, useRef, useState } from "react";
import { pubSub } from "../ably";
import { CHANNELS } from "../channel/channels.js";
import { normalizeUserChannelMessage } from "../channel/normalizePayload.js";

// The users channel holds one message per HCP registration and per Join / Remove, so the list
// is rebuilt from deep history: up to 10,000 messages (1,000 is Ably's largest page).
export const USERS_HISTORY = { limit: 1000, maxPages: 10 };

// Waits before reading a failed history again; after the last, the next connect retries.
export const SEED_RETRY_MS = [5000, 15000, 30000, 60000];

const toList = (byId) =>
  [...byId.values()].map(({ clientID, name, joinMeeting }) => ({ clientID, name, joinMeeting }));

/**
 * useHcpUsers - the HCP viewers on the session's users channel, each with their latest
 * status: users = [{ clientID, name, joinMeeting }] in the order they registered. Seeded from
 * history on mount and every reconnect, then kept live.
 * setJoinMeeting(clientID, joinMeeting) publishes { clientID, name, joinMeeting } for that one
 * viewer (their page follows it); the list shows it at once and goes back if it fails.
 */
export default function useHcpUsers(sessionId) {
  const [state, setState] = useState({ sessionId: null, users: [] });
  // clientID -> { clientID, name, joinMeeting, at }; at = Ably timestamp of that status.
  const sessionRef = useRef({ sessionId: null, byId: new Map() });

  const commit = useCallback(() => {
    const { sessionId: id, byId } = sessionRef.current;
    setState({ sessionId: id, users: toList(byId) });
  }, []);

  useEffect(() => {
    if (sessionId == null) return undefined;
    let cancelled = false;
    const byId = new Map();
    sessionRef.current = { sessionId, byId };

    // The newest message about a viewer wins; false when it changed nothing.
    const apply = (data, timestamp) => {
      const msg = normalizeUserChannelMessage(data);
      if (!msg) return false;
      const at = Number.isFinite(timestamp) ? timestamp : Date.now();
      const prev = byId.get(msg.clientID);
      if (prev && at < prev.at) return false;
      byId.set(msg.clientID, { ...msg, name: msg.name || prev?.name || "", at });
      return true;
    };

    const onMessage = (data, _isHistory, meta) => {
      if (!cancelled && apply(data, meta?.timestamp)) commit();
    };

    // A failed read is tried again after a wait - a steady connection may never reconnect,
    // and until then the list would miss everyone who registered before the page opened.
    let retryTimer = null;
    let retries = 0;
    const seed = async () => {
      clearTimeout(retryTimer);
      let items;
      try {
        // strict: a failed read must not pass for an empty channel.
        items = await pubSub.getChannelHistoryWithMeta(CHANNELS.USERS, { ...USERS_HISTORY, strict: true });
      } catch (e) {
        console.warn("[users] history read failed - trying again:", e?.message || e);
        if (!cancelled && retries < SEED_RETRY_MS.length) retryTimer = setTimeout(seed, SEED_RETRY_MS[retries++]);
        return;
      }
      if (cancelled || !items?.length) return;
      // Ably hands history back newest first: oldest first, each viewer ends on their latest.
      let changed = false;
      for (const item of [...items].reverse()) changed = apply(item.data, item.timestamp) || changed;
      if (changed) commit();
    };

    const onConnect = (connected) => {
      if (!connected) return;
      retries = 0;
      seed();
    };
    pubSub.subscribe(CHANNELS.USERS, onMessage);
    pubSub.onConnectionChange(onConnect);
    if (pubSub.isConnected) seed();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      pubSub.unsubscribe(CHANNELS.USERS, onMessage);
      pubSub.offConnectionChange(onConnect);
    };
  }, [sessionId, commit]);

  const setJoinMeeting = useCallback(
    (clientID, joinMeeting) => {
      const { byId } = sessionRef.current;
      const user = byId.get(clientID);
      if (!user || user.joinMeeting === joinMeeting) return;
      byId.set(clientID, { ...user, joinMeeting });
      commit();
      // async: a publish that throws right away (channel not attached) rejects instead.
      (async () => pubSub.publish(CHANNELS.USERS, { clientID, name: user.name, joinMeeting }))().catch((e) => {
        console.warn("[users] Join / Remove failed:", e?.message || e);
        // Back to what the channel says - unless a newer status came in meanwhile.
        const now = byId.get(clientID);
        if (now && now.at === user.at) {
          byId.set(clientID, { ...now, joinMeeting: user.joinMeeting });
          commit();
        }
      });
    },
    [commit],
  );

  return { users: state.sessionId === sessionId ? state.users : [], setJoinMeeting };
}
